/**
 * Live-trading one-click placement (live-tennis V1b).
 *
 * An explicitly ARMED interface state in which a ladder/pill price click on a
 * Kalshi market submits a limit order immediately — no ticket, no second
 * click. Design rules, in order of importance:
 *
 *   • Arming is SESSION-ONLY. It always starts disarmed and is never
 *     persisted. Refresh, access loss, identity change, route departure,
 *     board-context change, venue departure, and the toggle disarm it.
 *   • The one-click size is its own explicit setting (persisted), NOT the
 *     ticket's `defaultContracts`: that pref is deliberately blank-fill-only
 *     in the panel, and reusing it here would give the panel's anti-fat-finger
 *     rule a second meaning. Hard-clamped to ONE_CLICK_MAX_CONTRACTS — the
 *     largest explicit per-trader ceiling the relay accepts.
 *   • Submissions are paced by MIN_ORDER_SPACING_MS (the same ≤4/sec spacing
 *     the multi-leg ladder uses on the shared org Kalshi token bucket). A
 *     click landing inside the window is DROPPED and surfaced as "throttled",
 *     never queued — a queue would fire orders after the market moved.
 *   • Every order carries a fresh `lt-` client_order_id so the relay audit
 *     rows (and Kalshi's echo) can be joined back to one-click inputs during
 *     post-session reconciliation.
 *   • postOnly rides the click's ladder semantics: a bid-level click joins
 *     (post_only=true, can't cross), an ask-level click may cross. This store
 *     never overrides what the click site derived.
 *
 * The submit path is accountStore.placeOrder — the same audited relay lane
 * (checkOrderLimit → trades audit row → trackIntent) every manual ticket
 * order takes. There is no second order route.
 */

import { create } from 'zustand';
import {
  hasPendingKalshiPlacementConfirmation,
  useAccountStore,
} from './accountStore';
import { MIN_ORDER_SPACING_MS } from '../views/ValueDashboard/orderLadder';
import type { VenueBook } from '../views/ValueDashboard/types';
import { OrderSubmissionUnknownError } from '../lib/trading/orderSubmissionOutcome';
import { probeEnd, probeFinish, probeMark } from '../lib/trading/orderLatencyProbe';
import {
  buildLt2AuditMeta,
  lt2AuditRequired,
  evaluateIntentGuard,
  type LiveJumpIntent,
  type LiveOrderIntent,
} from '../lib/trading/liveIntent';
import { recordLt2Timing, updateLt2Timing, lt2ClockStamp, type Lt2ClockStamp } from '../lib/trading/lt2Timing';
import { nextClientOrderId } from '../lib/trading/coidPool';
import { sourceConfirmationError } from '../lib/trading/sourceConfirmation';
import { deriveAttackTarget } from '../lib/trading/attackTarget';
import { getChannelVolume, isChannelSoundEnabled } from '../lib/alerts/alertPreferences';
import { playOrderAckTone, playOrderRejectedTone } from '../lib/audio/tradeAudio';

/** Largest explicit per-trader one-click ceiling supported by the relay. */
export const ONE_CLICK_MAX_CONTRACTS = 2_147_483_647;
/** Conservative V1 fallback when no explicit per-trader ceiling is present. */
export const ONE_CLICK_FALLBACK_MAX_CONTRACTS = 500;
export const ONE_CLICK_DEFAULT_CONTRACTS = 100;

const SIZE_STORAGE_KEY = 'galactus:oneClickSizeContracts';

export interface OneClickAck {
  /** Monotonic per-session sequence so the UI can key transitions. */
  seq: number;
  /** 'unverified' (LT2-009): the venue accepted a resting order but it was
   *  no longer visible in the open-orders view 5s later — reconcile via
   *  receipts rather than assuming it is live OR dead.
   *  'partial' (#724): a two-sided press where one leg was venue-accepted and
   *  the other was not. Never collapsed into 'accepted' — the accepted leg is
   *  a live resting order the trader must now manage alone. */
  status: 'pending' | 'accepted' | 'rejected' | 'throttled' | 'unverified' | 'partial';
  /** e.g. "BUY 100 NO @ 24¢ SVITOLINA" — built by the click site. */
  label: string;
  message?: string;
  atMs: number;
  /** #724 — one line per leg of a two-sided press, in declared visible order
   *  (top/left first). Absent on every single-order ack. The strip renders
   *  each leg beneath the summary so a refused side is always visible. */
  legs?: OneClickLegAck[];
}

/** One leg of a two-sided (S) acknowledgement (#724). */
export interface OneClickLegAck {
  /** The sized leg label, e.g. "100× DAL −3.5 YES @ 47¢". */
  label: string;
  status: 'pending' | 'accepted' | 'rejected' | 'unverified';
  message?: string;
}

/** The most recent venue-accepted one-click placement — the reconciliation
 *  anchor for the 'unverified' check and any receipt deep-link. */
export interface OneClickLastAccepted {
  clientOrderId: string;
  venueOrderId: string | null;
  ticker: string;
  side: 'yes' | 'no';
  priceCents: number;
  atMs: number;
}

export interface OneClickSubmitInput {
  ticker: string;
  side: 'yes' | 'no';
  priceCents: number;
  postOnly: boolean;
  /** Short human label for the ack line (team/player + side). */
  label: string;
}

interface OneClickStore {
  armed: boolean;
  sizeContracts: number;
  lastAck: OneClickAck | null;
  lastAccepted: OneClickLastAccepted | null;
  arm: () => void;
  disarmReason: string | null;
  disarm: (reason?: string) => void;
  setSizeContracts: (n: number) => void;
  /** Fire a one-click order. No-op (with a truthful ack) when disarmed,
   *  throttled, or the input fails the local guards.
   *
   *  LEGACY V1 shape with no remaining production callsite: every armed
   *  mouse click routes through the intercept adapters into `submitIntent`,
   *  which is where the acknowledgement/timing parity contract lives. Kept
   *  for its pinned tests and as the documented V1 input; new callers use
   *  `submitIntent`. */
  submit: (input: OneClickSubmitInput) => Promise<void>;
  /** D-020 path: fire a normalized LT2 intent through the SUBMIT-TIME guard
   *  (identity, availability, price, maker-role revalidation against the
   *  current book) before the audited placement. Every refusal surfaces a
   *  truthful ack — never a silent drop while armed. */
  submitIntent: (
    intent: LiveOrderIntent,
    book: VenueBook | null | undefined,
    report?: PlacementReport,
  ) => Promise<void>;
  /** Truthful local refusal on the ack line for input paths that fail before
   *  an intent can even be built (hotkey with no displayed price / target).
   *  Never a silent no-op while armed. */
  rejectLocal: (label: string, message: string) => void;
  /** V3 Spacebar: post a non-order lifecycle ack (Pull All pending/summary)
   *  onto the same strip. Pending/accepted statuses only — order intents
   *  keep their own paths. */
  noteAck: (status: 'pending' | 'accepted' | 'rejected', label: string, message?: string) => void;
  /** Live Trading V3 W/E: fire a bounded ATTACK intent — the same guarded,
   *  audited lane as submitIntent, with the attack size, the `__attack` gate
   *  marker, and postOnly false (intentional taker within the bound). Refuses
   *  truthfully when attack is not armed. */
  submitAttackIntent: (
    intent: LiveOrderIntent,
    book: VenueBook | null | undefined,
    report?: PlacementReport,
  ) => Promise<void>;
  /** Speed-demon D1 Q/R: re-price ONE resting order to the displayed bid
   *  through `accountStore.replaceOrder` (the replace route — one relay
   *  round trip). Mirrors `submitIntent` exactly: submit-time guard, pacing,
   *  pooled coid, FETCH FIRST, then the pending ack / tone / timing entry.
   *  Size = the resting order's remaining, never the one-click size. A
   *  `REPLACE_AMBIGUOUS` outcome acks 'unverified' (the original may still
   *  rest) and is never retried. */
  submitJumpIntent: (
    intent: LiveJumpIntent,
    book: VenueBook | null | undefined,
    report?: PlacementReport,
  ) => Promise<void>;
  /** #724 two-sided S: fire the focused card's two PASSIVE legs (exactly the
   *  A and D intents, in that order) as one press. All-or-none PREFLIGHT —
   *  both legs pass the submit-time guard before either is sent — then both
   *  are placed concurrently through the same audited lane as submitIntent,
   *  each at the one-click size, each with its own `lt-` coid and the shared
   *  group stamp. Completion is NOT atomic: outcomes are reported per leg and
   *  summarized truthfully (accepted / partial / rejected / unverified); an
   *  accepted leg beside a refused one is never rolled back, hedged or
   *  resent. One press = one pacing window. */
  submitTwoSidedIntent: (args: {
    /** The group's ack label, e.g. "S DAL −3.5". */
    label: string;
    legs: readonly [LiveOrderIntent, LiveOrderIntent];
    books: readonly [VenueBook | null | undefined, VenueBook | null | undefined];
    report?: PlacementReport;
  }) => Promise<void>;
  /** #724 — an earlier S press with at least one side whose response was
   *  lost, so nobody knows whether it was placed. While it is set, the next S
   *  press is refused: resending both legs could double a side that is
   *  already live. An ACCEPTED leg that is not yet visible does not hold — it
   *  is known to exist at the venue. A, D, W and E are unaffected. Cleared
   *  when every lost side shows up in account state, or by disarming. */
  twoSidedHold: TwoSidedHold | null;
  /** Reconcile the hold against current account state, then return the
   *  refusal for a new S press, or null when S may fire. */
  twoSidedHoldReason: () => string | null;
}

/**
 * What one press put on the wire, told to the caller that fired it: each
 * accepted order's venue id (null when the venue answered without one), and
 * each order whose response was lost. Nothing is reported for a press that
 * never left the browser (refused, throttled, disarmed). The hotkey listener
 * uses it so Pull All can sweep exactly this press's orders if they land
 * after the pull's first pass.
 */
export interface PlacementReport {
  /** An accepted order: its venue id (null when the venue answered without
   *  one) and the market it was placed on. */
  placed: (venueOrderId: string | null, ticker: string) => void;
  lost: () => void;
}

/** The lost sides of the S press that set the hold, by their coids. */
export interface TwoSidedHold {
  /** The group's ack label, e.g. "S DAL/NYG ML". */
  label: string;
  legs: ReadonlyArray<{ label: string; clientOrderId: string }>;
}

/**
 * The jump ack's attempt identity: "<count>× <label> <from>¢ → <to>¢", e.g.
 * "100× BOS NO 44¢ → 46¢". The target label's own " @ 46¢" suffix is dropped
 * so the price appears once, as a move.
 */
export function jumpAckLabel(intent: LiveJumpIntent): string {
  const base = intent.label.replace(/ @ [\d.]+¢$/, '');
  return sizedAckLabel(
    `${base} ${intent.prevPriceCents}¢ → ${intent.priceCents}¢`,
    intent.prevRemaining,
  );
}

/** A replace whose cancel leg ended unknown — typed by code, not by class,
 *  so a mocked accountStore (tests) and a real one read the same. */
function isReplaceAmbiguous(err: unknown): boolean {
  return (
    err != null &&
    typeof err === 'object' &&
    (err as { code?: unknown }).code === 'REPLACE_AMBIGUOUS'
  );
}

/**
 * The ack line's frozen attempt identity: "<count>× <label>", e.g.
 * "100× DAL -3.5 YES @ 48¢". The count is the size actually sent, captured
 * once at entry and never re-read from the size input after dispatch, so
 * pending, accepted, rejected and unverified all name the same order even if
 * the trader edits the size mid-flight. The label is untouched.
 */
export function sizedAckLabel(label: string, count: number): string {
  return `${count}× ${label}`;
}

function clampSize(n: number): number {
  if (!Number.isFinite(n)) return ONE_CLICK_DEFAULT_CONTRACTS;
  return Math.max(1, Math.min(ONE_CLICK_MAX_CONTRACTS, Math.round(n)));
}

function loadSize(): number {
  try {
    const raw = localStorage.getItem(SIZE_STORAGE_KEY);
    const n = raw == null ? NaN : parseInt(raw, 10);
    return Number.isFinite(n) ? clampSize(n) : ONE_CLICK_DEFAULT_CONTRACTS;
  } catch {
    return ONE_CLICK_DEFAULT_CONTRACTS;
  }
}

let _ackSeq = 0;
let _lastSubmitAtMs = 0;

/** Registered by attackStore at ITS module init — a lazy accessor instead of
 *  an import, because attackStore already imports this store (for the arm
 *  subordination) and a top-level cycle left this module's exports
 *  uninitialized in attackStore's body. Null = attack machinery not loaded,
 *  which fails closed below exactly like a disarmed attack. */
let _attackState:
  | (() => { attackArmed: boolean; attackSizeContracts: number })
  | null = null;
/**
 * The LOCAL acknowledgement sound — rings the moment a valid intent passes
 * local preflight, for mouse and hotkey alike, which is the parity contract:
 * the first audible feedback must not wait for the relay round trip. It is
 * its OWN channel ('orderAck', a neutral tick) precisely because it fires
 * before the venue answers: the venue-accepted chirp ('orderPlaced') keeps
 * its registry meaning and still rings from placeOrder on success, and the
 * rejection descent stays the failure sound. Three states, three sounds,
 * each independently toggleable.
 */
function playLocalAckTone(): void {
  if (isChannelSoundEnabled('orderAck')) playOrderAckTone(getChannelVolume('orderAck'));
}

/** Audible local refusal — an armed trader's refused keypress must not be
 *  silent-to-the-ear when a click would have rung (perceived input lag).
 *  Shares the 'orderRejected' channel, whose registry description covers
 *  local guard refusals and venue rejections alike. */
function playLocalRefusalTone(): void {
  if (isChannelSoundEnabled('orderRejected')) {
    playOrderRejectedTone(getChannelVolume('orderRejected'));
  }
}

export function _registerAttackStateAccessor(
  fn: () => { attackArmed: boolean; attackSizeContracts: number },
): void {
  _attackState = fn;
}

/** How long after venue acceptance a claimed-resting order may stay invisible
 *  in the open-orders view before the ack flips to 'unverified' (LT2-009). */
export const ONE_CLICK_VERIFY_AFTER_MS = 5_000;

/**
 * LT2-009's evidence question, asked once per accepted resting order: is the
 * venue order visible in authoritative state? Resting is visible; a fill is
 * also a fully accounted-for exit. The placement fence temporarily re-injects
 * an accepted order into openOrders while venue reads lag; that projection
 * must not prove itself visible.
 */
function restingOrderVisible(venueOrderId: string): boolean {
  const account = useAccountStore.getState();
  const venueOrderVisible =
    !hasPendingKalshiPlacementConfirmation(venueOrderId) &&
    (account.openOrders ?? []).some((o) => o.orderId === venueOrderId);
  return (
    venueOrderVisible || (account.recentFills ?? []).some((f) => f.orderId === venueOrderId)
  );
}

/**
 * Has an S leg whose response was lost shown up in account state? It has no
 * venue id, so it is matched by its own `lt-` coid, which the venue echoes on
 * open-order reads. A leg that never appears keeps the hold until the trader
 * disarms: absence is not proof it was never placed (it may have filled).
 */
function twoSidedLegSeen(leg: { clientOrderId: string }): boolean {
  return (useAccountStore.getState().openOrders ?? []).some(
    (o) => o.clientOrderId === leg.clientOrderId,
  );
}

/** Pure: does this hold still refuse S right now? (No state change; the
 *  header readout asks it on render.) */
export function twoSidedHoldBlocks(hold: TwoSidedHold | null): boolean {
  return hold != null && !hold.legs.every(twoSidedLegSeen);
}

/** Per-leg placement result of a two-sided press, before summarization. */
type TwoSidedLegResult =
  | { status: 'accepted'; venueOrderId: string | null }
  | { status: 'rejected'; message: string }
  | { status: 'unverified'; message: string };

/**
 * The group verdict from its legs' verdicts (#724). `unverified` outranks
 * everything because a lost response is the one outcome the trader cannot act
 * on without looking; `partial` is its own word because "placed" would be a
 * lie about the refused side and "rejected" a lie about the resting one.
 */
export function summarizeTwoSided(
  results: readonly TwoSidedLegResult[],
): 'accepted' | 'partial' | 'rejected' | 'unverified' {
  if (results.some((r) => r.status === 'unverified')) return 'unverified';
  if (results.every((r) => r.status === 'accepted')) return 'accepted';
  if (results.some((r) => r.status === 'accepted')) return 'partial';
  return 'rejected';
}

export const useOneClickStore = create<OneClickStore>((set, get) => ({
  armed: false,
  sizeContracts: loadSize(),
  lastAck: null,
  lastAccepted: null,

  disarmReason: null,
  arm: () => set({ armed: true, lastAck: null, disarmReason: null }),
  // A MANUAL disarm (no reason: the arm toggle, or access loss) is also the
  // trader's deliberate reset of an S hold: they have been told to check
  // receipts. A board-driven disarm (it carries a reason) is not a decision
  // about the lost leg, so the hold survives it.
  disarm: (reason) =>
    set(
      reason == null
        ? { armed: false, lastAck: null, disarmReason: null, twoSidedHold: null }
        : { armed: false, lastAck: null, disarmReason: reason },
    ),

  twoSidedHold: null,
  twoSidedHoldReason: () => {
    const hold = get().twoSidedHold;
    if (hold == null) return null;
    if (hold.legs.every(twoSidedLegSeen)) {
      set({ twoSidedHold: null });
      return null;
    }
    return `Earlier ${hold.label} has a side not yet confirmed — check receipts, or disarm to reset`;
  },

  rejectLocal: (label: string, message: string) => {
    playLocalRefusalTone();
    set({
      lastAck: { seq: ++_ackSeq, status: 'rejected', label, message, atMs: Date.now() },
    });
  },

  noteAck: (status: 'pending' | 'accepted' | 'rejected', label: string, message?: string) => {
    if (status === 'rejected') playLocalRefusalTone();
    set({
      lastAck: { seq: ++_ackSeq, status, label, message, atMs: Date.now() },
    });
  },

  setSizeContracts: (n: number) => {
    const clamped = clampSize(n);
    set({ sizeContracts: clamped });
    try {
      localStorage.setItem(SIZE_STORAGE_KEY, String(clamped));
    } catch {
      /* private mode — session-only size is fine */
    }
  },

  submit: async (input: OneClickSubmitInput) => {
    const { armed, sizeContracts } = get();
    if (!armed) return; // click sites gate on armed; belt-and-suspenders
    const count = clampSize(sizeContracts);
    const ackLabel = sizedAckLabel(input.label, count);

    const ack = (partial: Omit<OneClickAck, 'seq' | 'atMs'>) =>
      set({ lastAck: { ...partial, seq: ++_ackSeq, atMs: Date.now() } });

    if (!Number.isInteger(input.priceCents) || input.priceCents < 1 || input.priceCents > 99) {
      ack({ status: 'rejected', label: ackLabel, message: 'Price out of range' });
      return;
    }

    // Pacing — drop, don't queue. The window is measured from the last
    // ACCEPTED submission attempt (not from throttled drops), so a rapid
    // double-click drops exactly the second click.
    const now = Date.now();
    if (now - _lastSubmitAtMs < MIN_ORDER_SPACING_MS) {
      ack({ status: 'throttled', label: ackLabel, message: 'Click ignored — pacing (≤4/sec)' });
      return;
    }
    _lastSubmitAtMs = now;

    // Fetch-first (same as submitIntent): the POST is dispatched before the
    // pending ack and the tone; the promise is awaited below.
    const placing = useAccountStore.getState().placeOrder({
      ticker: input.ticker,
      side: input.side,
      action: 'buy',
      type: 'limit',
      count,
      price: input.priceCents,
      postOnly: input.postOnly,
      clientOrderId: nextClientOrderId(),
    });
    ack({ status: 'pending', label: ackLabel });
    playLocalAckTone();
    try {
      await placing;
      ack({ status: 'accepted', label: ackLabel });
    } catch (err) {
      // Same distinction as submitIntent: a lost response is not a rejection.
      ack({
        status: err instanceof OrderSubmissionUnknownError ? 'unverified' : 'rejected',
        label: ackLabel,
        message: err instanceof Error && err.message ? err.message : 'Order failed',
      });
    }
  },

  submitIntent: async (
    intent: LiveOrderIntent,
    book: VenueBook | null | undefined,
    report?: PlacementReport,
  ) => {
    const { armed, sizeContracts } = get();
    if (!armed) return; // click sites gate on armed; belt-and-suspenders
    probeMark('submit');
    // Frozen at entry: the size this call will send, and the identity every
    // ack on this call carries. The size input may change while the request
    // is in flight; nothing below reads it again.
    const count = clampSize(sizeContracts);
    const ackLabel = sizedAckLabel(intent.label, count);

    const ack = (partial: Omit<OneClickAck, 'seq' | 'atMs'>): number => {
      const seq = ++_ackSeq;
      set({ lastAck: { ...partial, seq, atMs: Date.now() } });
      return seq;
    };

    // SUBMIT-TIME guard — the render-time pill disable is presentation, this
    // is the enforcement. Refusals are truthful acks, never silent drops.
    const guardAt = lt2ClockStamp();
    const guard = evaluateIntentGuard(intent, book);
    if (!guard.ok) {
      playLocalRefusalTone();
      ack({ status: 'rejected', label: ackLabel, message: guard.message });
      probeMark('refused-guard');
      probeFinish();
      const refusedAt = lt2ClockStamp();
      recordLt2Timing({
        inputMethod: intent.inputMethod,
        outcome: 'guard-refused',
        inputAt: intent.inputAtClock ?? null,
        guard: guardAt,
        ack: refusedAt,
        request: null,
        final: refusedAt,
        finalOutcome: 'terminal-at-ack',
      });
      return;
    }

    // Pacing — drop, don't queue (same window as `submit`).
    const now = Date.now();
    if (now - _lastSubmitAtMs < MIN_ORDER_SPACING_MS) {
      ack({ status: 'throttled', label: ackLabel, message: 'Click ignored — pacing (≤4/sec)' });
      probeMark('refused-pacing');
      probeFinish();
      const throttledAt = lt2ClockStamp();
      recordLt2Timing({
        inputMethod: intent.inputMethod,
        outcome: 'throttled',
        inputAt: intent.inputAtClock ?? null,
        guard: guardAt,
        ack: throttledAt,
        request: null,
        final: throttledAt,
        finalOutcome: 'terminal-at-ack',
      });
      return;
    }
    _lastSubmitAtMs = now;

    // Coid minted BEFORE the call so the audit stamp and the reconciliation
    // anchor share it even if the response is lost mid-flight. Pooled: the
    // submit path pops a pre-minted id instead of running randomUUID inline.
    const clientOrderId = nextClientOrderId();
    // FETCH FIRST (speed-demon Deploy C, browser pre-send). placeOrder is
    // async but dispatches the POST synchronously — postOrderRaw → apiFetch →
    // fetch() all run before their first await — so calling it here puts the
    // wire ahead of the pending-ack re-render, the ack tone and the timing
    // record. Nothing between the fence and this line does any work. The
    // promise is awaited below; requestAt is stamped right before the call.
    const requestAt = lt2ClockStamp();
    const placing = useAccountStore.getState().placeOrder({
      ticker: intent.ticker,
      side: intent.side,
      action: 'buy',
      type: 'limit',
      count,
      price: intent.priceCents,
      postOnly: intent.postOnly,
      clientOrderId,
      // V1 moneyline and N-outcome moneyline CLICKS also use submitIntent
      // for its stronger submit-time guard, but must not acquire the
      // separate LT2 capability boundary merely because the implementation
      // is shared. HOTKEY intents are that capability, so they are stamped
      // on every market kind (#647) — see lt2AuditRequired.
      auditMeta: lt2AuditRequired(intent)
        ? buildLt2AuditMeta(intent, guard, book)
        : undefined,
    });
    ack({ status: 'pending', label: ackLabel });
    // LOCAL acknowledgement — before the await, so the sound is the keypress's
    // echo, not the venue's (parity contract; venue truth follows separately).
    playLocalAckTone();
    const ackAt = lt2ClockStamp();
    // The timing entry is created NOW, at the local pending acknowledgement,
    // so its render stamp measures the first frame after THIS ack — the
    // input→first-render number the parity review needs — not the first
    // frame after the venue answered. Outcome and response are patched onto
    // this same entry when the relay replies.
    const timingId = recordLt2Timing({
      inputMethod: intent.inputMethod,
      outcome: 'pending',
      inputAt: intent.inputAtClock ?? null,
      guard: guardAt,
      ack: ackAt,
      request: requestAt,
    });
    try {
      const placed = await placing;
      probeEnd();
      const responseAt = lt2ClockStamp();
      const venueOrderId =
        placed != null && typeof placed.orderId === 'string' && placed.orderId !== ''
          ? placed.orderId
          : null;
      // LT2-009 reconciliation below decides the RESTING order's terminal
      // moment; a crossing (taker) or id-less acceptance is terminal at the
      // response itself.
      report?.placed(venueOrderId, intent.ticker);
      const expectRest = intent.postOnly || guard.role !== 'taker';
      const verifies = venueOrderId != null && expectRest;
      updateLt2Timing(timingId, {
        outcome: 'accepted',
        response: responseAt,
        ...(verifies ? {} : { final: responseAt, finalOutcome: 'accepted-terminal' }),
      });
      const acceptedSeq = ack({ status: 'accepted', label: ackLabel });
      set({
        lastAccepted: {
          clientOrderId,
          venueOrderId,
          ticker: intent.ticker,
          side: intent.side,
          priceCents: intent.priceCents,
          atMs: Date.now(),
        },
      });
      // LT2-009 reconciliation: a maker order the venue accepted should be
      // visible among open orders (placeOrder inserts confirmed resting
      // orders synchronously and refreshOrders re-reads). If it has vanished
      // shortly after acceptance without a newer ack, say so explicitly
      // instead of letting the trader assume it rests. Crossing intents
      // (role 'taker', non-postOnly) legitimately leave the book — skip.
      if (verifies) {
        setTimeout(() => {
          const st = get();
          if (st.lastAck?.seq !== acceptedSeq) {
            // A newer ack owns the line; this order's visibility question is
            // abandoned, and pretending it settled would be false telemetry.
            updateLt2Timing(timingId, { final: lt2ClockStamp(), finalOutcome: 'superseded' });
            return;
          }
          const account = useAccountStore.getState();
          // Resting is visible; a fill is also a fully accounted-for exit —
          // only authoritative evidence counts. The placement fence temporarily
          // re-injects an accepted order into openOrders while venue reads lag;
          // that projection must not prove itself visible.
          const venueOrderVisible =
            !hasPendingKalshiPlacementConfirmation(venueOrderId) &&
            (account.openOrders ?? []).some((o) => o.orderId === venueOrderId);
          const visible =
            venueOrderVisible ||
            (account.recentFills ?? []).some((f) => f.orderId === venueOrderId);
          if (!visible) {
            set({
              lastAck: {
                seq: ++_ackSeq,
                status: 'unverified',
                label: ackLabel,
                message: 'Accepted but not visible on the book — check receipts',
                atMs: Date.now(),
              },
            });
          }
          updateLt2Timing(timingId, {
            final: lt2ClockStamp(),
            finalOutcome: visible ? 'resting-verified' : 'unverified',
            ...(visible ? {} : { outcome: 'unverified' as const }),
          });
        }, ONE_CLICK_VERIFY_AFTER_MS);
      }
    } catch (err) {
      probeEnd();
      const outcome = err instanceof OrderSubmissionUnknownError ? 'unverified' : 'rejected';
      if (outcome === 'unverified') report?.lost();
      ack({
        status: outcome,
        label: ackLabel,
        message: err instanceof Error && err.message ? err.message : 'Order failed',
      });
      const failAt = lt2ClockStamp();
      updateLt2Timing(timingId, { outcome, response: failAt, final: failAt, finalOutcome: outcome });
    }
  },

  submitAttackIntent: async (
    intent: LiveOrderIntent,
    book: VenueBook | null | undefined,
    report?: PlacementReport,
  ) => {
    const { armed } = get();
    const attackState = _attackState?.() ?? null;
    // The listener gates on both arms; belt-and-suspenders here so no other
    // caller can fire an attack from a half-armed (or not-even-loaded) state.
    if (!armed || attackState == null || !attackState.attackArmed) return;
    const { attackSizeContracts } = attackState;

    const ack = (partial: Omit<OneClickAck, 'seq' | 'atMs'>): number => {
      const seq = ++_ackSeq;
      set({ lastAck: { ...partial, seq, atMs: Date.now() } });
      return seq;
    };

    // An attack intent must carry its own provenance — a missing payload is a
    // caller bug, refused rather than silently downgraded to a passive order.
    if (intent.attack == null || intent.postOnly) {
      playLocalRefusalTone();
      ack({ status: 'rejected', label: intent.label, message: 'Malformed attack intent' });
      return;
    }
    const guardAt = lt2ClockStamp();
    // ACTION-TIME PROOF: re-derive the bounded target from the book handed in
    // at THIS boundary, and refuse if it no longer matches what the keypress
    // computed. The listener's derivation is at most one render frame older,
    // but the packet's contract is a fresh snapshot at action time — recompute
    // OR block, never a stale bound. Blocking is the safe half: submitting a
    // silently recomputed price the trader never saw acknowledged would trade
    // one staleness for another.
    const freshTarget = deriveAttackTarget(book ?? null, intent.side, false);
    if (
      freshTarget == null ||
      freshTarget.targetPriceCents !== intent.priceCents ||
      freshTarget.referencePriceCents !== intent.attack.referencePriceCents ||
      freshTarget.depth !== intent.attack.depth
    ) {
      playLocalRefusalTone();
      ack({
        status: 'rejected',
        label: intent.label,
        message: 'Book changed — attack not sent; re-press at the new level',
      });
      const refusedAt = lt2ClockStamp();
      recordLt2Timing({
        inputMethod: intent.inputMethod,
        outcome: 'guard-refused',
        inputAt: intent.inputAtClock ?? null,
        guard: guardAt,
        ack: refusedAt,
        request: null,
        final: refusedAt,
        finalOutcome: 'terminal-at-ack',
      });
      return;
    }

    // Same submit-time guard as every other intent: identity, availability,
    // price range, and role. postOnly is false, so an intentional taker role
    // passes — the aggression bound lives in the resolver-computed target
    // (reference + accepted depth) and the relay's attack ceiling.
    const guard = evaluateIntentGuard(intent, book);
    if (!guard.ok) {
      playLocalRefusalTone();
      ack({ status: 'rejected', label: intent.label, message: guard.message });
      const refusedAt = lt2ClockStamp();
      recordLt2Timing({
        inputMethod: intent.inputMethod,
        outcome: 'guard-refused',
        inputAt: intent.inputAtClock ?? null,
        guard: guardAt,
        ack: refusedAt,
        request: null,
        final: refusedAt,
        finalOutcome: 'terminal-at-ack',
      });
      return;
    }

    const now = Date.now();
    if (now - _lastSubmitAtMs < MIN_ORDER_SPACING_MS) {
      ack({ status: 'throttled', label: intent.label, message: 'Keypress ignored — pacing (≤4/sec)' });
      const throttledAt = lt2ClockStamp();
      recordLt2Timing({
        inputMethod: intent.inputMethod,
        outcome: 'throttled',
        inputAt: intent.inputAtClock ?? null,
        guard: guardAt,
        ack: throttledAt,
        request: null,
        final: throttledAt,
        finalOutcome: 'terminal-at-ack',
      });
      return;
    }
    _lastSubmitAtMs = now;

    // The ATTACK size, never the one-click size. The Attack store normalizes
    // persisted values; the relay enforces the current per-trader ceiling.
    const count = Math.max(1, Math.round(attackSizeContracts));
    const clientOrderId = nextClientOrderId();
    // FETCH FIRST — same ordering as submitIntent: the POST leaves before the
    // pending ack, the tone and the timing record; awaited below.
    const requestAt = lt2ClockStamp();
    const placing = useAccountStore.getState().placeOrder({
      ticker: intent.ticker,
      side: intent.side,
      action: 'buy',
      type: 'limit',
      count,
      price: intent.priceCents,
      postOnly: false,
      clientOrderId,
      // Every hotkey intent is LT2-stamped (lt2AuditRequired); the attack
      // marker is ADDITIONAL — the relay's gate input and the durable
      // record of reference/depth this bounded action was computed from.
      auditMeta: buildLt2AuditMeta(intent, guard, book),
      // The durable action-time evidence: the marker the relay's arithmetic
      // gate checks, plus the minimal exact book snapshot the target was
      // proven against — both raw sides' top levels, so a reader can
      // reconstruct the chosen side's reference, the target, and the
      // opposing liquidity at it without trusting any derived field.
      attackMeta: {
        depth: intent.attack.depth,
        referencePriceCents: intent.attack.referencePriceCents,
        book: {
          atMs: Date.now(),
          marketTicker: book?.marketTicker ?? null,
          freshness: book?.freshness ?? null,
          lastPriceCents: book?.lastPriceCents ?? null,
          yes: (book?.yes ?? []).slice(0, 3).map((l) => ({ p: l.priceCents, s: l.size })),
          no: (book?.no ?? []).slice(0, 3).map((l) => ({ p: l.priceCents, s: l.size })),
        },
      },
    });
    ack({ status: 'pending', label: intent.label });
    playLocalAckTone();
    const ackAt = lt2ClockStamp();
    // Same contract as submitIntent: the entry exists from the local pending
    // acknowledgement onward, so renderMs measures the ack frame, never the
    // response frame.
    const timingId = recordLt2Timing({
      inputMethod: intent.inputMethod,
      outcome: 'pending',
      inputAt: intent.inputAtClock ?? null,
      guard: guardAt,
      ack: ackAt,
      request: requestAt,
    });
    try {
      const placed = await placing;
      const responseAt = lt2ClockStamp();
      const venueOrderId =
        placed != null && typeof placed.orderId === 'string' && placed.orderId !== ''
          ? placed.orderId
          : null;
      // LT2-009 reconciliation applies to the ESTABLISH half only: a taker
      // fill legitimately never rests. guard.role is 'taker' when displayed
      // opposing liquidity crossed the target — skip; maker/unknown means the
      // order should be visible resting at the target.
      report?.placed(venueOrderId, intent.ticker);
      const expectRest = guard.role !== 'taker';
      const verifies = venueOrderId != null && expectRest;
      updateLt2Timing(timingId, {
        outcome: 'accepted',
        response: responseAt,
        ...(verifies ? {} : { final: responseAt, finalOutcome: 'accepted-terminal' }),
      });
      const acceptedSeq = ack({ status: 'accepted', label: intent.label });
      set({
        lastAccepted: {
          clientOrderId,
          venueOrderId,
          ticker: intent.ticker,
          side: intent.side,
          priceCents: intent.priceCents,
          atMs: Date.now(),
        },
      });
      if (verifies) {
        setTimeout(() => {
          const st = get();
          if (st.lastAck?.seq !== acceptedSeq) {
            updateLt2Timing(timingId, { final: lt2ClockStamp(), finalOutcome: 'superseded' });
            return;
          }
          const account = useAccountStore.getState();
          const venueOrderVisible =
            !hasPendingKalshiPlacementConfirmation(venueOrderId) &&
            (account.openOrders ?? []).some((o) => o.orderId === venueOrderId);
          const visible =
            venueOrderVisible ||
            (account.recentFills ?? []).some((f) => f.orderId === venueOrderId);
          if (!visible) {
            set({
              lastAck: {
                seq: ++_ackSeq,
                status: 'unverified',
                label: intent.label,
                message: 'Accepted but not visible on the book — check receipts',
                atMs: Date.now(),
              },
            });
          }
          updateLt2Timing(timingId, {
            final: lt2ClockStamp(),
            finalOutcome: visible ? 'resting-verified' : 'unverified',
            ...(visible ? {} : { outcome: 'unverified' as const }),
          });
        }, ONE_CLICK_VERIFY_AFTER_MS);
      }
    } catch (err) {
      const outcome = err instanceof OrderSubmissionUnknownError ? 'unverified' : 'rejected';
      if (outcome === 'unverified') report?.lost();
      ack({
        status: outcome,
        label: intent.label,
        message: err instanceof Error && err.message ? err.message : 'Order failed',
      });
      const failAt = lt2ClockStamp();
      updateLt2Timing(timingId, { outcome, response: failAt, final: failAt, finalOutcome: outcome });
    }
  },

  submitJumpIntent: async (
    intent: LiveJumpIntent,
    book: VenueBook | null | undefined,
    report?: PlacementReport,
  ) => {
    const { armed } = get();
    if (!armed) return; // the listener gates on armed; belt-and-suspenders
    probeMark('submit');
    // Frozen at entry: the size this jump carries is the resting order's
    // remaining as the card resolved it — a re-price never resizes — and the
    // identity every ack on this call names.
    const count = clampSize(intent.prevRemaining);
    const ackLabel = jumpAckLabel(intent);

    const ack = (partial: Omit<OneClickAck, 'seq' | 'atMs'>): number => {
      const seq = ++_ackSeq;
      set({ lastAck: { ...partial, seq, atMs: Date.now() } });
      return seq;
    };
    const refuse = (message: string, guardAt: Lt2ClockStamp) => {
      playLocalRefusalTone();
      ack({ status: 'rejected', label: ackLabel, message });
      probeMark('refused-guard');
      probeFinish();
      const refusedAt = lt2ClockStamp();
      recordLt2Timing({
        inputMethod: intent.inputMethod,
        outcome: 'guard-refused',
        inputAt: intent.inputAtClock ?? null,
        guard: guardAt,
        ack: refusedAt,
        request: null,
        final: refusedAt,
        finalOutcome: 'terminal-at-ack',
      });
    };

    const guardAt = lt2ClockStamp();
    // A jump must name the order it moves; a caller that cannot is a bug,
    // refused rather than downgraded to a fresh post.
    if (
      typeof intent.replaceOrderId !== 'string' ||
      intent.replaceOrderId === '' ||
      !Number.isFinite(intent.prevPriceCents) ||
      !Number.isInteger(intent.prevRemaining) ||
      intent.prevRemaining < 1
    ) {
      refuse('Malformed jump intent', guardAt);
      return;
    }
    // The listener refuses this first; re-checked at the boundary so no other
    // caller can spend a venue round trip moving an order nowhere.
    if (intent.priceCents === intent.prevPriceCents) {
      refuse('Already at the displayed price', guardAt);
      return;
    }
    // SUBMIT-TIME guard — identity, availability, price range, maker-role
    // revalidation against the CURRENT book. Same enforcement as submitIntent.
    const guard = evaluateIntentGuard(intent, book);
    if (!guard.ok) {
      refuse(guard.message, guardAt);
      return;
    }

    // Pacing — drop, don't queue (same window as every submit path).
    const now = Date.now();
    if (now - _lastSubmitAtMs < MIN_ORDER_SPACING_MS) {
      ack({ status: 'throttled', label: ackLabel, message: 'Keypress ignored — pacing (≤4/sec)' });
      probeMark('refused-pacing');
      probeFinish();
      const throttledAt = lt2ClockStamp();
      recordLt2Timing({
        inputMethod: intent.inputMethod,
        outcome: 'throttled',
        inputAt: intent.inputAtClock ?? null,
        guard: guardAt,
        ack: throttledAt,
        request: null,
        final: throttledAt,
        finalOutcome: 'terminal-at-ack',
      });
      return;
    }
    _lastSubmitAtMs = now;

    // Coid minted BEFORE the call so the audit stamp and the reconciliation
    // anchor share it even if the response is lost mid-flight.
    const clientOrderId = nextClientOrderId();
    // FETCH FIRST — replaceOrder dispatches the POST before its first await
    // (validation is synchronous), so the wire is ahead of the pending-ack
    // re-render, the tone and the timing record. Awaited below.
    const requestAt = lt2ClockStamp();
    const replacing = useAccountStore.getState().replaceOrder(
      intent.replaceOrderId,
      intent.priceCents,
      count,
      {
        clientOrderId,
        // Every hotkey intent is LT2-stamped (lt2AuditRequired). The stamp
        // rides the replace body as `__lt2` and lands on both audit rows.
        auditMeta: lt2AuditRequired(intent)
          ? {
              ...buildLt2AuditMeta(intent, guard, book),
              jump: {
                replaceOrderId: intent.replaceOrderId,
                prevPriceCents: intent.prevPriceCents,
                prevRemaining: intent.prevRemaining,
              },
            }
          : undefined,
      },
    );
    ack({ status: 'pending', label: ackLabel });
    // LOCAL acknowledgement — before the await, so the sound is the
    // keypress's echo, not the venue's (parity contract).
    playLocalAckTone();
    const ackAt = lt2ClockStamp();
    const timingId = recordLt2Timing({
      inputMethod: intent.inputMethod,
      outcome: 'pending',
      inputAt: intent.inputAtClock ?? null,
      guard: guardAt,
      ack: ackAt,
      request: requestAt,
    });
    try {
      const result = await replacing;
      probeEnd();
      const responseAt = lt2ClockStamp();
      const venueOrderId =
        result != null && typeof result.orderId === 'string' && result.orderId !== ''
          ? result.orderId
          : null;
      // A jump is post-only, so the replacement rests or is refused — there
      // is no crossing case. LT2-009 reconciliation applies whenever an id
      // came back.
      // #724 Pull All second pass (main parity): the replacement's venue id is
      // this press's order, so a pull that snapshotted before it landed can
      // sweep it by id. Reported after the await — never on the pre-send path.
      report?.placed(venueOrderId, intent.ticker);
      const verifies = venueOrderId != null;
      updateLt2Timing(timingId, {
        outcome: 'accepted',
        response: responseAt,
        ...(verifies ? {} : { final: responseAt, finalOutcome: 'accepted-terminal' }),
      });
      const acceptedSeq = ack({ status: 'accepted', label: ackLabel });
      set({
        lastAccepted: {
          clientOrderId,
          venueOrderId,
          ticker: intent.ticker,
          side: intent.side,
          priceCents: intent.priceCents,
          atMs: Date.now(),
        },
      });
      if (verifies) {
        setTimeout(() => {
          const st = get();
          if (st.lastAck?.seq !== acceptedSeq) {
            updateLt2Timing(timingId, { final: lt2ClockStamp(), finalOutcome: 'superseded' });
            return;
          }
          const account = useAccountStore.getState();
          const venueOrderVisible =
            !hasPendingKalshiPlacementConfirmation(venueOrderId) &&
            (account.openOrders ?? []).some((o) => o.orderId === venueOrderId);
          const visible =
            venueOrderVisible ||
            (account.recentFills ?? []).some((f) => f.orderId === venueOrderId);
          if (!visible) {
            set({
              lastAck: {
                seq: ++_ackSeq,
                status: 'unverified',
                label: ackLabel,
                message: 'Accepted but not visible on the book — check receipts',
                atMs: Date.now(),
              },
            });
          }
          updateLt2Timing(timingId, {
            final: lt2ClockStamp(),
            finalOutcome: visible ? 'resting-verified' : 'unverified',
            ...(visible ? {} : { outcome: 'unverified' as const }),
          });
        }, ONE_CLICK_VERIFY_AFTER_MS);
      }
    } catch (err) {
      probeEnd();
      // Two unknown-outcome classes, one ack: a lost response (the venue may
      // hold either order) and an AMBIGUOUS cancel leg (the original may
      // still rest). Neither is a rejection, and neither may be retried.
      const outcome =
        err instanceof OrderSubmissionUnknownError || isReplaceAmbiguous(err)
          ? 'unverified'
          : 'rejected';
      if (outcome === 'unverified') report?.lost();
      ack({
        status: outcome,
        label: ackLabel,
        message: err instanceof Error && err.message ? err.message : 'Jump failed',
      });
      const failAt = lt2ClockStamp();
      updateLt2Timing(timingId, {
        outcome,
        response: failAt,
        final: failAt,
        finalOutcome: outcome,
      });
    }
  },

  submitTwoSidedIntent: async ({ label, legs, books, report }) => {
    const { armed, sizeContracts } = get();
    if (!armed) return; // the listener gates on armed; belt-and-suspenders
    // Frozen at entry, shared by both legs: one press, one size.
    const count = clampSize(sizeContracts);
    const legLabels = legs.map((leg) => sizedAckLabel(leg.label, count));

    const ack = (partial: Omit<OneClickAck, 'seq' | 'atMs'>): number => {
      const seq = ++_ackSeq;
      set({ lastAck: { ...partial, seq, atMs: Date.now() } });
      return seq;
    };
    const refuse = (message: string, legAcks: OneClickLegAck[]): void => {
      playLocalRefusalTone();
      ack({ status: 'rejected', label, message, legs: legAcks });
    };
    const refusedTiming = (leg: LiveOrderIntent, guardAt: Lt2ClockStamp) => {
      const refusedAt = lt2ClockStamp();
      recordLt2Timing({
        inputMethod: leg.inputMethod,
        outcome: 'guard-refused',
        inputAt: leg.inputAtClock ?? null,
        guard: guardAt,
        ack: refusedAt,
        request: null,
        final: refusedAt,
        finalOutcome: 'terminal-at-ack',
      });
    };

    // A two-sided press is PASSIVE by definition (#724: "the two passive
    // top-of-book postings that correspond to A and D"). A crossing leg would
    // pay the spread the pair exists to capture, and the listener already
    // refuses it; this is the boundary's own assertion, not a downgrade.
    const guardAt = lt2ClockStamp();
    if (legs.some((leg) => !leg.postOnly || leg.attack != null)) {
      refuse('S posts both sides passively — switch the card to maker pricing', [
        { label: legLabels[0], status: 'rejected', message: 'not sent' },
        { label: legLabels[1], status: 'rejected', message: 'not sent' },
      ]);
      legs.forEach((leg) => refusedTiming(leg, guardAt));
      return;
    }

    // ALL-OR-NONE PREFLIGHT. Both legs are guarded against their current books
    // before either is sent; one refusal refuses the press. This is the only
    // place a two-sided press is atomic — everything after the wire is not.
    // The live source check `placeOrder` makes at send time is asked here
    // too, for BOTH legs: the card's books can be a frame older than the
    // market store, and a leg that placeOrder would refuse must refuse the
    // press, not become a one-sided order.
    const guards = legs.map((leg, i): ReturnType<typeof evaluateIntentGuard> => {
      const guard = evaluateIntentGuard(leg, books[i]);
      if (!guard.ok) return guard;
      const source = sourceConfirmationError({ ticker: leg.ticker });
      return source == null
        ? guard
        : { ok: false, code: 'stale', message: 'Source confirmation unavailable — order not sent' };
    });
    const failed = guards.findIndex((g) => !g.ok);
    if (failed !== -1) {
      const legAcks = guards.map((g, i): OneClickLegAck =>
        g.ok
          ? { label: legLabels[i], status: 'rejected', message: 'not sent — other side refused' }
          : { label: legLabels[i], status: 'rejected', message: g.message },
      );
      // The summary names the side; the leg line beneath it carries the
      // reason, once. Repeating the reason on both lines doubled the strip's
      // height for no extra information (visual pass, Sep 22).
      refuse(`${legs[failed].label} refused — nothing sent`, legAcks);
      legs.forEach((leg) => refusedTiming(leg, guardAt));
      return;
    }
    const okGuards = guards.map((g) => {
      if (!g.ok) throw new Error('unreachable: preflight failure escaped');
      return g;
    });

    // Pacing — ONE window per press, not per leg. Measured like every other
    // submission; the next press (any key) is paced from this stamp.
    const now = Date.now();
    if (now - _lastSubmitAtMs < MIN_ORDER_SPACING_MS) {
      ack({ status: 'throttled', label, message: 'Keypress ignored — pacing (≤4/sec)' });
      const throttledAt = lt2ClockStamp();
      legs.forEach((leg) =>
        recordLt2Timing({
          inputMethod: leg.inputMethod,
          outcome: 'throttled',
          inputAt: leg.inputAtClock ?? null,
          guard: guardAt,
          ack: throttledAt,
          request: null,
          final: throttledAt,
          finalOutcome: 'terminal-at-ack',
        }),
      );
      return;
    }
    _lastSubmitAtMs = now;

    // Identities minted BEFORE either call: one group id shared by both legs,
    // one `lt-` coid each (pooled — popped, never minted inline on this
    // path), so the audit rows can be joined back into the pair even if a
    // response is lost mid-flight.
    const groupId = crypto.randomUUID();
    const clientOrderIds = legs.map(() => nextClientOrderId());
    const stamped = legs.map(
      (leg, i): LiveOrderIntent => ({
        ...leg,
        group: { id: groupId, mode: 'two-sided', leg: i, of: legs.length },
      }),
    );

    // FETCH FIRST (speed-demon Deploy C), for BOTH legs: placeOrder dispatches
    // its POST before its first await, so both requests are on the wire ahead
    // of the pending-ack re-render, the tone and the timing records — the same
    // order submitIntent keeps. CONCURRENT: "both sides at once" is the
    // request. Each leg is the ordinary audited placeOrder call submitIntent
    // makes, with the same size and the same audit builder; only the group
    // stamp is new. Each leg's response is stamped when THAT leg settles.
    // On node1's fast lane the relay's per-user lock covers only the limit
    // check + intent reservation, so the two legs reach Kalshi concurrently;
    // near a cap the second can still be refused (a 'partial' press).
    const requestAt = lt2ClockStamp();
    const responseAts: Lt2ClockStamp[] = [];
    const placing = stamped.map((leg, i) =>
      useAccountStore
        .getState()
        .placeOrder({
          ticker: leg.ticker,
          side: leg.side,
          action: 'buy',
          type: 'limit',
          count,
          price: leg.priceCents,
          postOnly: leg.postOnly,
          clientOrderId: clientOrderIds[i],
          // Every hotkey intent is LT2-stamped (lt2AuditRequired); the group
          // stamp rides inside the same durable record.
          auditMeta: buildLt2AuditMeta(leg, okGuards[i], books[i]),
        })
        .finally(() => {
          responseAts[i] = lt2ClockStamp();
        }),
    );

    // The group's own ack sequence: every write the group makes moves it, so a
    // leg's later verification can tell "the group still owns the line" from
    // "something else has spoken since" without abandoning its sibling's
    // question the moment the first leg flips to unverified.
    let groupSeq = ack({
      status: 'pending',
      label,
      legs: legLabels.map((l): OneClickLegAck => ({ label: l, status: 'pending' })),
    });
    // LOCAL acknowledgement — once, after dispatch and before the awaits: the
    // keypress's echo.
    playLocalAckTone();
    const ackAt = lt2ClockStamp();
    // One LT2 timing sample PER LEG, each patched with its own response.
    const timingIds = stamped.map((leg) =>
      recordLt2Timing({
        inputMethod: leg.inputMethod,
        outcome: 'pending',
        inputAt: leg.inputAtClock ?? null,
        guard: guardAt,
        ack: ackAt,
        request: requestAt,
      }),
    );
    const settled = await Promise.allSettled(placing);
    const results = settled.map((outcome, i): TwoSidedLegResult => {
      const responseAt = responseAts[i] ?? lt2ClockStamp();
      if (outcome.status === 'fulfilled') {
        const placed = outcome.value;
        const venueOrderId =
          placed != null && typeof placed.orderId === 'string' && placed.orderId !== ''
            ? placed.orderId
            : null;
        updateLt2Timing(timingIds[i], {
          outcome: 'accepted',
          response: responseAt,
          ...(venueOrderId == null ? { final: responseAt, finalOutcome: 'accepted-terminal' } : {}),
        });
        return { status: 'accepted', venueOrderId };
      }
      const err: unknown = outcome.reason;
      const status = err instanceof OrderSubmissionUnknownError ? 'unverified' : 'rejected';
      const message = err instanceof Error && err.message ? err.message : 'Order failed';
      updateLt2Timing(timingIds[i], {
        outcome: status,
        response: responseAt,
        final: responseAt,
        finalOutcome: status,
      });
      return { status, message };
    });

    const legAcks = results.map(
      (r, i): OneClickLegAck =>
        r.status === 'accepted'
          ? { label: legLabels[i], status: 'accepted' }
          : { label: legLabels[i], status: r.status, message: r.message },
    );
    const summary = summarizeTwoSided(results);
    results.forEach((r, i) => {
      if (r.status === 'accepted') report?.placed(r.venueOrderId, legs[i].ticker);
      else if (r.status === 'unverified') report?.lost();
    });
    // A lost response holds S until that side is seen (or the trader resets).
    const lostLegs = results.flatMap((r, i) =>
      r.status === 'unverified'
        ? [{ label: legLabels[i], clientOrderId: clientOrderIds[i] }]
        : [],
    );
    if (lostLegs.length > 0) set({ twoSidedHold: { label, legs: lostLegs } });
    // One sentence for the PRESS; each leg line beneath it carries its own
    // reason exactly once, so a refusal is never printed twice.
    const unknownLeg = results.findIndex((r) => r.status === 'unverified');
    groupSeq = ack({
      status: summary,
      label,
      message:
        summary === 'accepted'
          ? undefined
          : summary === 'partial'
            ? 'one side not placed'
            : summary === 'unverified'
              ? `${legs[unknownLeg].label} — response lost; check receipts`
              : 'neither side placed',
      legs: legAcks,
    });
    // The reconciliation anchor is the LAST accepted leg in declared order —
    // one anchor, as before; both legs are on the receipts by their coids.
    results.forEach((r, i) => {
      if (r.status !== 'accepted') return;
      set({
        lastAccepted: {
          clientOrderId: clientOrderIds[i],
          venueOrderId: r.venueOrderId,
          ticker: legs[i].ticker,
          side: legs[i].side,
          priceCents: legs[i].priceCents,
          atMs: Date.now(),
        },
      });
    });

    // LT2-009 per accepted leg: every S leg is post-only, so every accepted
    // leg with a venue id is expected to REST and is checked for visibility.
    results.forEach((r, i) => {
      if (r.status !== 'accepted' || r.venueOrderId == null) return;
      const venueOrderId = r.venueOrderId;
      setTimeout(() => {
        const st = get();
        if (st.lastAck?.seq !== groupSeq) {
          // Something other than this group owns the line now.
          updateLt2Timing(timingIds[i], { final: lt2ClockStamp(), finalOutcome: 'superseded' });
          return;
        }
        const visible = restingOrderVisible(venueOrderId);
        if (!visible) {
          const current = st.lastAck;
          const nextLegs = (current?.legs ?? legAcks).map((l, j) =>
            j === i ? { ...l, status: 'unverified' as const, message: 'not visible on the book' } : l,
          );
          groupSeq = ack({
            status: 'unverified',
            label,
            message: `${legs[i].label} accepted but not visible — check receipts`,
            legs: nextLegs,
          });
        }
        updateLt2Timing(timingIds[i], {
          final: lt2ClockStamp(),
          finalOutcome: visible ? 'resting-verified' : 'unverified',
          ...(visible ? {} : { outcome: 'unverified' as const }),
        });
      }, ONE_CLICK_VERIFY_AFTER_MS);
    });
  },
}));
